import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import type { ProcessMapGraph } from "@gk/shared";
import { api, downloadAuthed, t } from "../../lib/api";
import { useAsync, usePoll, fmtDate } from "../../lib/hooks";
import { Empty, ErrorNote, ProcessMap, SeverityPill, StatusPill } from "../../components/ui";
import type { Engagement } from "../Engagement";

interface Dashboard {
  progress: { planned: number; completed: number; verified: number; inReview: number; scheduled: number };
  coverage: Array<{ department: string; location: string; planned: number; completed: number; verified: number }>;
  map: ProcessMapGraph | null;
  mapVersion: number | null;
  findings: Array<{ title: string; kind: string; severity: string | null; mentions: number; departments: string[]; locations: string[] }>;
  breakdowns: Array<{ dim: string; value: string | null; total: number; done: number; verified: number }>;
  modules: Array<{ key: string; name: string; disclaimer: string; items: Array<{ key: string; area: string; check: string; counts: Record<string, number> }> }>;
  recent: Array<{ id: string; status: string; startedAt: string | null; pseudonym: string; department: string | null; location: string | null }>;
  latency: { median: number | null; n: number };
  viewer: { role: string; client: boolean };
}

const KIND: Record<string, string> = { gap: "Gap", risk: "Risk", quick_win: "Quick win" };
const DIM: Record<string, string> = { designation: "By role", stakeholderType: "By stakeholder group", language: "By language" };

export function DashboardTab({ engagement }: { engagement: Engagement; reload: () => void }) {
  const { tenant } = useParams();
  const d = useAsync(() => api.get<Dashboard>(`${t(tenant!)}/engagements/${engagement.id}/dashboard`), [tenant, engagement.id]);
  usePoll(() => void d.reload(), 15_000);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);

  if (d.error) return <ErrorNote error={d.error} />;
  if (!d.data) return <p className="muted">Loading…</p>;
  const x = d.data;
  const planned = Math.max(x.progress.planned, x.progress.completed + x.progress.scheduled);
  const pct = (n: number) => (planned ? `${(n / planned) * 100}%` : "0%");
  const recordedNotReviewed = Math.max(0, x.progress.completed - x.progress.verified - x.progress.inReview);

  const exportPdf = async () => {
    setPdfBusy(true);
    setNote(null);
    try {
      const { id } = await api.post<{ id: string }>(`${t(tenant!)}/engagements/${engagement.id}/reports`, { lang: engagement.reportLanguage });
      for (let i = 0; i < 90; i++) {
        await new Promise((r) => setTimeout(r, 1500));
        const r = await api.get<{ status: string; error: string | null }>(`${t(tenant!)}/reports/${id}`);
        if (r.status === "done") {
          await downloadAuthed(`${t(tenant!)}/reports/${id}/download`, `${engagement.name} — progress.pdf`);
          return;
        }
        if (r.status === "failed") throw new Error(r.error ?? "The report could not be produced.");
      }
      setNote("The report is taking longer than usual. It will appear under reports when ready.");
    } catch (err) {
      setNote((err as Error).message);
    } finally {
      setPdfBusy(false);
    }
  };

  const locations = [...new Set(x.coverage.map((c) => c.location))];
  const departments = [...new Set(x.coverage.map((c) => c.department))];

  return (
    <div className="stack" style={{ gap: 20 }}>
      <section className="panel">
        <div className="panel-head">
          <h3>Interview progress</h3>
          <p>{x.latency.median != null ? `Median translation delay ${(x.latency.median / 1000).toFixed(1)} s across ${x.latency.n} turns` : ""}</p>
          <span className="spacer" />
          <button className="btn small" onClick={exportPdf} disabled={pdfBusy}>
            {pdfBusy ? "Preparing PDF…" : "Download progress report"}
          </button>
        </div>
        <div className="progress-bar" role="img" aria-label={`${x.progress.verified} verified, ${x.progress.inReview} in review, ${recordedNotReviewed} processing, of ${planned} planned`}>
          <i style={{ width: pct(x.progress.verified), background: "var(--forest)" }} />
          <i style={{ width: pct(x.progress.inReview), background: "var(--ochre)" }} />
          <i style={{ width: pct(recordedNotReviewed), background: "#9cb8aa" }} />
        </div>
        <div className="legend">
          <span>
            <b>{planned}</b>planned
          </span>
          <span>
            <i style={{ background: "#9cb8aa" }} />
            <b>{x.progress.completed}</b>recorded
          </span>
          <span>
            <i style={{ background: "var(--ochre)" }} />
            <b>{x.progress.inReview}</b>in review
          </span>
          <span>
            <i style={{ background: "var(--forest)" }} />
            <b>{x.progress.verified}</b>verified
          </span>
        </div>
        {note && <p className="note-box" style={{ marginTop: 12 }}>{note}</p>}
      </section>

      <div className="grid2">
        <section className="panel">
          <div className="panel-head">
            <h3>Coverage</h3>
            <p>Recorded of planned, by department and location</p>
          </div>
          {x.coverage.length === 0 ? (
            <Empty title="No interview plan yet">Add departments and locations under Plan and people.</Empty>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <table className="coverage">
                <thead>
                  <tr>
                    <th />
                    {locations.map((l) => (
                      <th key={l}>{l}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {departments.map((dep) => (
                    <tr key={dep}>
                      <th>{dep}</th>
                      {locations.map((loc) => {
                        const c = x.coverage.find((v) => v.department === dep && v.location === loc);
                        if (!c) return <td key={loc} style={{ background: "transparent" }} />;
                        const ratio = c.planned ? Math.min(1, c.completed / c.planned) : c.completed ? 1 : 0;
                        const uncovered = c.planned > 0 && c.completed === 0;
                        return (
                          <td
                            key={loc}
                            title={`${dep}, ${loc}: ${c.completed} recorded, ${c.verified} verified, ${c.planned} planned`}
                            style={{
                              background: `color-mix(in srgb, var(--forest) ${Math.round(ratio * 78)}%, #edf2ee)`,
                              color: ratio > 0.55 ? "#fff" : "var(--ink)",
                              outline: uncovered ? "2px solid var(--ochre)" : undefined,
                              outlineOffset: -2,
                            }}
                          >
                            {c.completed}/{c.planned || "–"}
                            <small>{c.verified} verified</small>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
              <p className="muted xs" style={{ marginTop: 8 }}>
                Cells outlined in ochre have no interviews yet.
              </p>
            </div>
          )}
        </section>
        <section className="panel">
          <div className="panel-head">
            <h3>Who we have heard from</h3>
          </div>
          <div className="grid2" style={{ gap: 18 }}>
            {(["designation", "stakeholderType", "language"] as const).map((dim) => {
              const rows = x.breakdowns.filter((b) => b.dim === dim);
              if (!rows.length) return null;
              return (
                <div key={dim}>
                  <h4 style={{ marginBottom: 6 }}>{DIM[dim]}</h4>
                  {rows.map((r) => (
                    <div key={r.value ?? "none"} className="row small" style={{ padding: "3px 0" }}>
                      <span>{r.value ?? "Not recorded"}</span>
                      <span className="spacer" />
                      <span className="muted">
                        {r.done} · {r.verified} verified
                      </span>
                    </div>
                  ))}
                </div>
              );
            })}
          </div>
        </section>
      </div>

      <section className="panel">
        <div className="panel-head">
          <h3>Current-state process</h3>
          <p>{x.map ? `Version ${x.mapVersion}, built from verified interviews only. Updates as each interview is verified.` : ""}</p>
        </div>
        {x.map ? (
          <ProcessMap graph={x.map} />
        ) : (
          <Empty title="The map appears after the first verified interview">Interviews are mapped once a reviewer has checked and verified them.</Empty>
        )}
      </section>

      <div className="grid2">
        <section className="panel">
          <div className="panel-head">
            <h3>Emerging findings</h3>
            <p>Raised in verified interviews, most often first</p>
          </div>
          {x.findings.length === 0 ? (
            <Empty title="Nothing verified yet" />
          ) : (
            x.findings.map((f, i) => (
              <div className="finding" key={i}>
                <span className="mentions" title={`${f.mentions} interviews`}>
                  {f.mentions}
                </span>
                <div>
                  <div style={{ fontWeight: 600 }}>{f.title}</div>
                  <div className="muted xs">{[...f.departments, ...f.locations].join(" · ")}</div>
                </div>
                <div className="row" style={{ gap: 6 }}>
                  <span className="pill">{KIND[f.kind] ?? f.kind}</span>
                  <SeverityPill severity={f.severity} />
                </div>
              </div>
            ))
          )}
        </section>
        {!x.viewer.client && (
          <section className="panel">
            <div className="panel-head">
              <h3>Latest interviews</h3>
            </div>
            {x.recent.length === 0 ? (
              <Empty title="No interviews yet" />
            ) : (
              x.recent.map((r) => (
                <div className="finding" key={r.id} style={{ gridTemplateColumns: "1fr auto" }}>
                  <div>
                    <Link to={`/w/${tenant}/i/${r.id}`} style={{ fontWeight: 600, textDecoration: "none" }}>
                      {r.pseudonym}
                    </Link>
                    <div className="muted xs">
                      {[r.department, r.location].filter(Boolean).join(", ")} · {fmtDate(r.startedAt)}
                    </div>
                  </div>
                  <StatusPill status={r.status} />
                </div>
              ))
            )}
          </section>
        )}
      </div>

      {x.modules.map((m) => (
        <section className="panel" key={m.key}>
          <div className="panel-head">
            <h3>{m.name}</h3>
            <p>{m.disclaimer}</p>
          </div>
          <table className="table checkmatrix">
            <thead>
              <tr>
                <th>Area</th>
                <th>In place</th>
                <th>Partial</th>
                <th>Gap</th>
                <th>Not discussed</th>
              </tr>
            </thead>
            <tbody>
              {m.items.map((it) => (
                <tr key={it.key}>
                  <td>
                    <b>{it.area}</b>
                    <div className="muted xs">{it.check}</div>
                  </td>
                  <td className="c-ok">{it.counts.ok ?? 0}</td>
                  <td className="c-partial">{it.counts.partial ?? 0}</td>
                  <td className="c-gap">{it.counts.gap ?? 0}</td>
                  <td className="muted">{it.counts.unknown ?? 0}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="muted xs" style={{ marginTop: 8 }}>
            Counts are interviews. {x.viewer.client ? "Verified interviews only." : "Includes interviews still in review."}
          </p>
        </section>
      ))}
    </div>
  );
}
